import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, CircleUser, Download, KeyRound, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { CompanyLogo } from "@/components/BrandMark";
import { pageLabel, promptInstall, setTitle, useInstall } from "@/lib/pwa";
import { IosInstallSheet } from "@/components/pwa/InstallButton";
import { cn } from "@/lib/utils";
import { HeaderGreeting, resetGreetingIntro } from "@/components/layout/HeaderGreeting";
import { ImpersonationBanner } from "@/components/admin/Impersonation";

export const useSignOut = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return async () => {
    await logout();
    resetGreetingIntro();
    const slug = localStorage.getItem("pnl_login_slug");
    navigate(slug ? `/login/${slug}` : "/login", { replace: true });
  };
};

/** Company logo with the name directly under it, top-left; doubles as the company switcher for multi-company users. */
const HeaderBrand = () => {
  const { user } = useAuth();
  const { company, lightLogoUrl, switchCompany } = useCompany();
  const multi = user?.is_super_admin || user?.companies?.length > 1;
  const showLogo = company.header_logo !== false && lightLogoUrl;
  const showName = company.header_name !== false;
  const block = (
    <span className="flex min-w-0 max-w-full flex-col items-start overflow-hidden text-left" data-testid="header-brand">
      {showLogo && <img src={lightLogoUrl} alt={`${company.name} logo`} className="h-6 max-w-[min(120px,100%)] object-contain object-left sm:h-7 sm:max-w-[min(150px,100%)]" data-testid="header-brand-logo" />}
      {showName && <span className="flex max-w-full items-center gap-1 text-[13px] font-semibold leading-tight text-slate-800" data-testid="header-brand-name">
        <span className="truncate">{company.name}</span>{multi && <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
      </span>}
      <HeaderGreeting />
    </span>
  );
  if (!multi) return block;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="block min-h-[44px] min-w-0 max-w-full rounded-md px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" data-testid="company-switcher">{block}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[70dvh] w-72 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-slate-500">Switch company</DropdownMenuLabel>
        {(user.companies || []).map((c) => (
          <DropdownMenuItem key={c.id} onSelect={() => c.id !== company.id && switchCompany(c.id, user.is_task_user ? "/work" : "/dashboard")}
            className={cn("gap-2 py-2", c.id === company.id && "bg-slate-50 font-semibold")} data-testid={`company-switch-${c.slug}`}>
            <CompanyLogo company={c} className={c.has_logo ? "h-5 w-10" : "h-5 w-5 rounded-full text-[9px]"} testid={`company-switch-logo-${c.slug}`} />
            <span className="truncate">{c.name}{c.status !== "active" ? " (inactive)" : ""}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Desktop: account menu. Mobile: opens the navigation drawer (which carries the same account items). */
const AccountMenu = ({ onMenu }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const mode = useInstall();
  const [ios, setIos] = useState(false);
  return (<>
    <IosInstallSheet open={ios} onOpenChange={setIos} />
    <button type="button" className="hdr-btn hdr-menu inline-flex lg:hidden" onClick={onMenu} aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-6 w-6" strokeWidth={2.4} /></button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="hdr-btn hdr-menu hidden lg:inline-flex" aria-label="Account menu" data-testid="account-menu-button"><Menu className="h-[22px] w-[22px]" strokeWidth={2.4} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal"><div className="truncate text-sm font-semibold text-slate-900" data-testid="user-email">{user?.name || user?.email}</div><div className="truncate text-xs text-slate-500" data-testid="user-role">{user?.role_label || (user?.is_super_admin ? "Super admin" : "Company admin")}</div></DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/profile")} className="gap-2" data-testid="menu-profile"><CircleUser className="h-4 w-4" /> Profile</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate("/profile#trusted-devices")} className="gap-2" data-testid="menu-trusted-devices"><KeyRound className="h-4 w-4" /> Trusted devices</DropdownMenuItem>
        {mode && <DropdownMenuItem onSelect={() => (mode === "prompt" ? promptInstall() : setIos(true))} className="gap-2" data-testid="menu-install"><Download className="h-4 w-4" /> Install app</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signOut} className="gap-2 text-red-600" data-testid="logout-button"><LogOut className="h-4 w-4" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </>);
};

export const TopBar = ({ onMenu }) => {
  const { company } = useCompany();
  const { user } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => { setTitle(company?.name, pageLabel(pathname)); }, [company?.name, pathname]);
  const ref = useRef(null);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => document.documentElement.style.setProperty("--topbar-h", `${Math.round(e.target.getBoundingClientRect().height)}px`));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return (
    <header ref={ref} className="pt-safe sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md" data-testid="top-bar">
      {user?.impersonated_by && <ImpersonationBanner user={user} company={company?.name} />}
      <div className="flex min-h-14 items-center gap-2 px-2 py-1 sm:min-h-16 sm:gap-3 sm:px-4 sm:py-0.5">
        <div className="min-w-0 flex-1 pl-1"><HeaderBrand /></div>
        <NotificationBell />
        <AccountMenu onMenu={onMenu} />
      </div>
    </header>
  );
};
